import { call, put, takeLatest } from 'redux-saga/effects';
import * as T from './eodClickhouseConstants';
import * as actions from './eodClickhouseActions';
import { getEodSummaryReportApi } from './eodClickhouseApi';
import {
  EOD_SUMMARY_DEFAULT_PAGE,
  EOD_PAGE_SIZE_DESKTOP,
  type EodSummaryReportNormalised,
  type EodSummaryReportPayload,
  type EodSummaryReportRow,
} from '../../../interface/eodClickhouseInterface';

/**
 * Resolve the row count across the whole range.
 *
 * The backend is expected to send `totalElements` (or one of its usual aliases)
 * alongside the page of rows. When it sends none, fall back to a count derived
 * from the current page — `rows.length === size` means there is probably at
 * least one more page, so add a page's worth to keep "Next" reachable rather
 * than stranding the user on page 1.
 */
function resolveTotalElements(raw: any, rows: EodSummaryReportRow[], page: number, size: number): number {
  for (const key of ['totalElements', 'totalRecords', 'totalCount']) {
    const n = Number(raw?.[key]);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  const consumed = Math.max(0, page - 1) * size + rows.length;
  return rows.length === size ? consumed + size : consumed;
}

/**
 * Unwrap + normalise one `eodSummaryReport` body. Some report endpoints wrap
 * the payload in `metaDataInfo.data`; both shapes are handled and every field
 * is guaranteed so the reducer never sees undefined.
 */
function normaliseEodSummaryResponse(response: any, payload: EodSummaryReportPayload): EodSummaryReportNormalised {
  const raw = response?.metaDataInfo?.data ?? response;
  const rows: EodSummaryReportRow[] = Array.isArray(raw?.rows) ? raw.rows : [];
  const page = Number(payload.page) || EOD_SUMMARY_DEFAULT_PAGE;
  const size = Number(payload.size) || EOD_PAGE_SIZE_DESKTOP;
  const totalElements = resolveTotalElements(raw, rows, page, size);
  const totalPages = Number(raw?.totalPages) || Math.max(1, Math.ceil(totalElements / size));
  return { rows, totals: raw?.totals ?? null, totalElements, totalPages };
}

function* fetchEodSummaryReportSaga(
  action: ReturnType<typeof actions.fetchEodSummaryReportRequest>
) {
  try {
    const response: any = yield call(getEodSummaryReportApi, action.payload);
    yield put(actions.fetchEodSummaryReportSuccess(normaliseEodSummaryResponse(response, action.payload)));
  } catch (error: any) {
    yield put(actions.fetchEodSummaryReportFailure(error?.message || 'Failed to fetch EOD summary report'));
  }
}

/**
 * Funds table. Same endpoint and same normaliser as the Summary Report above —
 * only the request parameters (its own page / size / sort) and the store slice
 * it lands in differ, so the two tables never overwrite each other's rows.
 */
function* fetchEodFundsReportSaga(
  action: ReturnType<typeof actions.fetchEodFundsReportRequest>
) {
  try {
    const response: any = yield call(getEodSummaryReportApi, action.payload);
    yield put(actions.fetchEodFundsReportSuccess(normaliseEodSummaryResponse(response, action.payload)));
  } catch (error: any) {
    yield put(actions.fetchEodFundsReportFailure(error?.message || 'Failed to fetch EOD funds report'));
  }
}

export default function* eodClickhouseSaga() {
  yield takeLatest(T.FETCH_EOD_SUMMARY_REPORT_REQUEST, fetchEodSummaryReportSaga);
  yield takeLatest(T.FETCH_EOD_FUNDS_REPORT_REQUEST, fetchEodFundsReportSaga);
}
